import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { api } from "@/api/client";
import { useAsyncData } from "@/hooks/useAsyncData";
import EventCard from "@/components/EventCard";
import ErrorState from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import { ArrowRight, Ticket, Shield, Star, Loader2 } from "lucide-react";

export default function Home() {
  const { data, loading, error, reload } = useAsyncData(
    () => Promise.all([api.siteContent.get(), api.events.list({ limit: 6 })]),
    [],
  );
  const siteContent = data?.[0] ?? null;
  const events = data?.[1] ?? [];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        <img
          src="/hero.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 opacity-80" />
        {siteContent?.image_url && (
          <img src={siteContent.image_url} alt="Hero override" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm mb-6 border border-white/20">
            <Star className="h-4 w-4 text-yellow-300" />
            <span>Secure ticketing powered by Square</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight tracking-tight">
            {siteContent?.title || "Discover Amazing Events"}
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            {siteContent?.subtitle || "Find and book tickets for the best events, seminars, and conferences."}
          </p>
          {siteContent?.body_text && (
            <p className="text-base text-white/65 mb-10 max-w-xl mx-auto">{siteContent.body_text}</p>
          )}
          <Link to={createPageUrl("Events")}>
            <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-10 h-14 text-lg rounded-full shadow-xl">
              Browse Events <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Video */}
      {siteContent?.video_url && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe src={siteContent.video_url} title="Video" className="w-full h-full" allowFullScreen />
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Why EventPass?</h2>
            <p className="text-gray-500 mt-2">Everything you need to attend great events</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Ticket, title: "Instant Tickets", desc: "Purchase in seconds, receive your digital ticket and QR code immediately.", color: "bg-indigo-100 text-indigo-600" },
              { icon: Shield, title: "Secure Payments", desc: "All transactions are encrypted and processed safely through Square.", color: "bg-green-100 text-green-600" },
              { icon: Star, title: "Easy Entry", desc: "Present your QR code at the door for a fast and seamless check-in.", color: "bg-amber-100 text-amber-600" },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="text-center p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-md transition-all duration-200">
                <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Upcoming Events</h2>
              <p className="text-gray-500 mt-1">Don't miss out on these experiences</p>
            </div>
            <Link to={createPageUrl("Events")}>
              <Button variant="outline" className="hidden sm:flex items-center gap-2 rounded-full">
                View All <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
            </div>
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => <EventCard key={event.id} event={event} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg">No upcoming events yet</p>
            </div>
          )}

          <div className="text-center mt-10">
            <Link to={createPageUrl("Events")}>
              <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8">
                View All Events <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}